/**
 * MCP (Model Context Protocol) Servers View
 * Manage MCP server connections, testing, and health monitoring
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Server,
  Plus,
  Play,
  Square,
  Trash2,
  RefreshCw,
  Search,
  CheckCircle,
  AlertCircle,
  XCircle,
  Zap,
  Globe,
  Cpu,
  HardDrive,
  Edit,
  Settings,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";

interface McpServer {
  id: string;
  name: string;
  type: "http" | "websocket" | "stdio";
  endpoint: string;
  status: "connected" | "disconnected" | "error" | "connecting";
  latency: number;
  uptime: string;
  lastCheck: string;
  capabilities: string[];
  tools: number;
  resources: number;
  prompts: number;
  config: any;
}

const MOCK_SERVERS: McpServer[] = [
  {
    id: "1",
    name: "File System MCP",
    type: "stdio",
    endpoint: "/usr/local/bin/mcp-server-fs",
    status: "connected",
    latency: 12,
    uptime: "45d 12h 30m",
    lastCheck: "Just now",
    capabilities: ["read", "write", "list", "search"],
    tools: 8,
    resources: 3,
    prompts: 2,
    config: { allowedPaths: ["/home", "/data"] },
  },
  {
    id: "2",
    name: "GitHub MCP",
    type: "http",
    endpoint: "http://localhost:3001/mcp/github",
    status: "connected",
    latency: 45,
    uptime: "12d 8h 15m",
    lastCheck: "Just now",
    capabilities: ["repo", "issues", "prs", "code"],
    tools: 12,
    resources: 4,
    prompts: 3,
    config: { token: "ghp_***", rateLimit: 5000 },
  },
  {
    id: "3",
    name: "Database MCP",
    type: "websocket",
    endpoint: "ws://localhost:3002/mcp/database",
    status: "error",
    latency: 0,
    uptime: "0d 0h 0m",
    lastCheck: "5m ago",
    capabilities: ["query", "schema", "analyze"],
    tools: 5,
    resources: 2,
    prompts: 1,
    config: { connectionString: "postgresql://***" },
  },
  {
    id: "4",
    name: "Weather MCP",
    type: "http",
    endpoint: "http://localhost:3003/mcp/weather",
    status: "disconnected",
    latency: 0,
    uptime: "0d 0h 0m",
    lastCheck: "1h ago",
    capabilities: ["current", "forecast", "alerts"],
    tools: 3,
    resources: 1,
    prompts: 2,
    config: { apiKey: "weather_***", units: "metric" },
  },
];

export function McpServersView() {
  const { addLogEntry } = useDashboardStore();
  const [servers, setServers] = useState<McpServer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "connected" | "disconnected" | "error">("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState<McpServer | null>(null);

  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      setServers(MOCK_SERVERS);
    } catch (error) {
      console.error("Failed to load MCP servers:", error);
      addLogEntry({
        category: "error",
        action: "Load MCP Servers",
        details: String(error),
        severity: "error",
        source: "mcp-servers",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const connectServer = async (id: string) => {
    try {
      setServers(s => s.map(srv => 
        srv.id === id ? { ...srv, status: "connecting" as const } : srv
      ));
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setServers(s => s.map(srv => 
        srv.id === id 
          ? { ...srv, status: "connected" as const, latency: Math.floor(Math.random() * 50) + 10, uptime: "0d 0h 1m" }
          : srv
      ));
      
      addLogEntry({
        category: "mcp",
        action: "Server Connected",
        details: `MCP server ${id} connected`,
        severity: "info",
        source: "mcp-servers",
      });
    } catch (error) {
      console.error("Failed to connect server:", error);
    }
  };

  const disconnectServer = async (id: string) => {
    try {
      setServers(s => s.map(srv => 
        srv.id === id ? { ...srv, status: "disconnected" as const, latency: 0 } : srv
      ));
      
      addLogEntry({
        category: "mcp",
        action: "Server Disconnected",
        details: `MCP server ${id} disconnected`,
        severity: "info",
        source: "mcp-servers",
      });
    } catch (error) {
      console.error("Failed to disconnect server:", error);
    }
  };

  const deleteServer = async (id: string) => {
    try {
      setServers(s => s.filter(srv => srv.id !== id));
      
      addLogEntry({
        category: "mcp",
        action: "Server Deleted",
        details: `MCP server ${id} deleted`,
        severity: "warning",
        source: "mcp-servers",
      });
    } catch (error) {
      console.error("Failed to delete server:", error);
    }
  };

  const checkAllServers = async () => {
    for (const server of servers) {
      if (server.status === "connected") {
        setServers(s => s.map(srv => 
          srv.id === server.id 
            ? { ...srv, latency: Math.floor(Math.random() * 50) + 10, lastCheck: "Just now" }
            : srv
        ));
      }
    }
  };

  const filteredServers = servers.filter(server => {
    const matchesSearch = 
      server.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      server.endpoint.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || server.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status: McpServer["status"]) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "connecting":
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case "disconnected":
        return <XCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: McpServer["status"]) => {
    switch (status) {
      case "connected":
        return <span className="rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-medium text-green-500">Connected</span>;
      case "connecting":
        return <span className="rounded-full bg-blue-500/10 px-2.5 py-1 text-xs font-medium text-blue-500">Connecting...</span>;
      case "error":
        return <span className="rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-500">Error</span>;
      case "disconnected":
        return <span className="rounded-full bg-gray-500/10 px-2.5 py-1 text-xs font-medium text-gray-500">Disconnected</span>;
    }
  };

  const getTypeIcon = (type: McpServer["type"]) => {
    switch (type) {
      case "http":
        return <Globe className="h-4 w-4 text-blue-500" />;
      case "websocket":
        return <Zap className="h-4 w-4 text-purple-500" />;
      case "stdio":
        return <Cpu className="h-4 w-4 text-green-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Server className="mx-auto h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))]" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Loading MCP servers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">MCP Servers</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Manage Model Context Protocol server connections</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={checkAllServers}
            className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
          >
            <RefreshCw className="h-4 w-4" />
            Check All
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            Add Server
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <Server className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{servers.length}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Total Servers</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{servers.filter(s => s.status === "connected").length}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Connected</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
              <HardDrive className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {servers.reduce((sum, s) => sum + s.tools, 0)}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Available Tools</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
              <Zap className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {servers.filter(s => s.status === "connected" && s.latency).length > 0
                  ? Math.round(servers.filter(s => s.status === "connected" && s.latency).reduce((sum, s) => sum + s.latency, 0) / servers.filter(s => s.status === "connected" && s.latency).length)
                  : 0}ms
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Avg Latency</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center justify-between rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <input
            type="text"
            placeholder="Search servers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
        </div>
        
        <div className="flex gap-2">
          {(["all", "connected", "disconnected", "error"] as const).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={cn(
                "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                statusFilter === status
                  ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                  : "hover:bg-[hsl(var(--muted))]"
              )}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Servers Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence>
          {filteredServers.map((server, index) => (
            <motion.div
              key={server.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: index * 0.05 }}
              className={cn(
                "rounded-xl border bg-[hsl(var(--card))] p-6",
                server.status === "error" ? "border-red-500/50" : "border-[hsl(var(--border))]"
              )}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-lg",
                    server.status === "connected" ? "bg-green-500/10" : "bg-gray-500/10"
                  )}>
                    {getTypeIcon(server.type)}
                  </div>
                  <div>
                    <h3 className="font-semibold">{server.name}</h3>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{server.type}</p>
                  </div>
                </div>
                {getStatusIcon(server.status)}
              </div>

              {/* Endpoint */}
              <div className="mb-4 rounded-md bg-[hsl(var(--muted))] p-3">
                <code className="text-xs font-mono break-all">{server.endpoint}</code>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                <div>
                  <p className="text-lg font-bold">{server.tools}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Tools</p>
                </div>
                <div>
                  <p className="text-lg font-bold">{server.resources}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Resources</p>
                </div>
                <div>
                  <p className="text-lg font-bold">{server.prompts}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Prompts</p>
                </div>
              </div>

              {/* Capabilities */}
              <div className="mb-4">
                <p className="mb-2 text-xs text-[hsl(var(--muted-foreground))]">Capabilities</p>
                <div className="flex flex-wrap gap-1.5">
                  {server.capabilities.map((cap) => (
                    <span
                      key={cap}
                      className="rounded bg-[hsl(var(--muted))] px-2 py-0.5 text-xs font-medium"
                    >
                      {cap}
                    </span>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))] mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {server.status === "connected" ? (
                      <>
                        <div className="h-2 w-2 rounded-full bg-green-500" />
                        <span>{server.latency}ms</span>
                      </>
                    ) : (
                      <span>{server.status}</span>
                    )}
                  </div>
                  <div>Uptime: {server.uptime}</div>
                </div>
                <span>{server.lastCheck}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {server.status === "connected" ? (
                  <>
                    <button
                      onClick={() => disconnectServer(server.id)}
                      className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-[hsl(var(--border))] py-2 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                    >
                      <Square className="h-4 w-4" />
                      Disconnect
                    </button>
                    <button
                      onClick={() => setShowConfigModal(server)}
                      className="flex items-center justify-center rounded-lg border border-[hsl(var(--border))] p-2 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => connectServer(server.id)}
                    disabled={server.status === "connecting"}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[hsl(var(--primary))] py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] disabled:opacity-50 hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
                  >
                    {server.status === "connecting" ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4" />
                        Connect
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => deleteServer(server.id)}
                  className="flex items-center justify-center rounded-lg border border-[hsl(var(--border))] p-2 hover:bg-red-500/10 hover:text-red-500 transition-colors cursor-pointer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filteredServers.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[hsl(var(--border))] p-12 text-center">
          <Server className="h-12 w-12 text-[hsl(var(--muted-foreground))]" />
          <h3 className="mt-4 text-lg font-semibold">No MCP servers found</h3>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Add a new server to get started</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            Add Server
          </button>
        </div>
      )}
    </div>
  );
}
